(() => {
  'use strict';

  class GWPManager {
    constructor() {
      // Get settings from theme settings
      const config = window.gwpConfig || {};
      this.enabled = config.gwp_enabled || false;
      this.gwpVariantId = config.gwp_variant_id || null;
      this.gwpNoteText = config.gwp_note_text || 'Free Gift';
      this.gwpNoteColor = config.gwp_note_color || '#000000';
      this.gwpThreshold = config.gwp_threshold || 1;
      
      // Flags to prevent infinite loops
      this.isProcessing = false;
      this.isAddingGWP = false;
      this.isRemovingGWP = false;
      this.hasCheckedOnce = false;
      
      // SessionStorage key for tracking manual GWP removal
      this.sessionStorageKey = 'gwp_manually_removed';
      
      // Debounce timer
      this.checkTimeout = null;
      this.observerTimeout = null;
      
      // MutationObserver for Rebuy cart
      this.rebuyObserver = null;
      this.rebuyRetryCount = 0;
      this.rebuyCartContainer = null;
      
      // Cache selectors
      this.selectors = {
        rebuyGWPItem: '.rebuy-cart__flyout li.rebuy-cart__flyout-item[class*="property-_gwp_product"]',
        rebuyCart: '.rebuy-cart__flyout, .rebuy-cart, [id*="rebuy-cart"], [class*="rebuy-cart"]',
        cartDrawer: 'cart-drawer'
      };
      
      if (!this.enabled || !this.gwpVariantId) return;
      
      this.init();
    }

    init() {
      // Unified cart update handler
      const handleCartUpdate = () => {
        if (!this.isProcessing) {
          this.debouncedCheck();
        }
      };
      
      document.addEventListener('cart:updated', handleCartUpdate);
      document.addEventListener('cart-drawer:updated', handleCartUpdate);

      // Listen for Rebuy Smart Cart events
      const rebuyEvents = [
        'rebuy:smartcart.init',
        'rebuy:smartcart.ready',
        'rebuy:smartcart.line-item-increase',
        'rebuy:smartcart.line-item-decrease',
        'rebuy:smartcart.line-item-removed',
        'rebuy:smartcart.show',
        'rebuy:smartcart.hide',
        'rebuy:cart.change'
      ];
      
      rebuyEvents.forEach(eventName => {
        document.addEventListener(eventName, (event) => {
          // Don't process if we're currently adding/removing GWP
          if (this.isProcessing || this.isAddingGWP || this.isRemovingGWP) {
            return;
          }
          
          const variantId = event.detail?.variant_id ? parseInt(event.detail.variant_id) : null;
          
          // Handle GWP removal detection
          if (eventName === 'rebuy:smartcart.line-item-removed' && variantId === this.gwpVariantId) {
            this.setGWPManuallyRemoved();
            return;
          }
          
          // Skip if GWP is being modified
          if (variantId === this.gwpVariantId) {
            return;
          }
          
          // On init/ready, do immediate check; others use debounce
          if (eventName === 'rebuy:smartcart.init' || eventName === 'rebuy:smartcart.ready') {
            this.checkAndAddGWP();
            // Initialize observer once on init/ready
            if (!this.rebuyObserver) {
              this.startObservingRebuyCart();
            }
          } else {
            this.debouncedCheck(300);
          }
        });
      });
      
      // Fallback: Check on DOM ready if Rebuy events haven't fired yet
      const fallbackCheck = () => {
        setTimeout(() => {
          if (!this.hasCheckedOnce) {
            this.checkAndAddGWP();
          }
        }, 500);
      };
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fallbackCheck);
      } else {
        fallbackCheck();
      }
      
      // Prevent manual addition of GWP through Rebuy
      this.preventManualGWPAddition();
      
      // Detect manual removal of GWP (works for both Rebuy and standard cart)
      this.detectManualGWPRemoval();
      
      // Start observing Rebuy cart for GWP items (if not already started)
      if (!this.rebuyObserver) {
        this.startObservingRebuyCart();
      }
    }
    
    setGWPManuallyRemoved() {
      try {
        sessionStorage.setItem(this.sessionStorageKey, 'true');
      } catch (e) {
        console.error('GWP: Failed to set sessionStorage', e);
      }
    }
    
    isGWPManuallyRemoved() {
      try {
        return sessionStorage.getItem(this.sessionStorageKey) === 'true';
      } catch (e) {
        return false;
      }
    }
    
    clearGWPManuallyRemoved() {
      try {
        sessionStorage.removeItem(this.sessionStorageKey);
      } catch (e) {
        console.error('GWP: Failed to clear sessionStorage', e);
      }
    }
    
    detectManualGWPRemoval() {
      // Listen for clicks on remove buttons (works for both Rebuy and standard cart)
      document.addEventListener('click', (event) => {
        // Rebuy remove buttons
        const rebuyRemoveButton = event.target.closest('.rebuy-cart__flyout-item-remove, [data-remove], button[class*="remove"]');
        // Standard cart remove buttons
        const standardRemoveButton = event.target.closest('cart-remove-button, [data-index]');
        
        const removeButton = rebuyRemoveButton || standardRemoveButton;
        if (removeButton) {
          // Find the line item containing this remove button
          const rebuyLineItem = removeButton.closest('.rebuy-cart__flyout-item, li[class*="rebuy-cart"]');
          const standardLineItem = removeButton.closest('.cart-item, [id^="CartItem-"]');
          const lineItem = rebuyLineItem || standardLineItem;
          
          if (lineItem) {
            // Check if this is a GWP item
            // For Rebuy: check property class
            if (lineItem.classList.toString().includes('property-_gwp_product')) {
              this.setGWPManuallyRemoved();
            }
            // For standard cart: check cart data to see if this is GWP
            else if (standardLineItem) {
              this.checkIfGWPItemByCart(standardLineItem).then(isGWP => {
                if (isGWP) {
                  this.setGWPManuallyRemoved();
                }
              });
            }
          }
        }
      }, true); // Use capture phase to catch early
    }
    
    async checkIfGWPItemByCart(lineItem) {
      try {
        // First check DOM for GWP properties (faster, synchronous)
        const properties = lineItem.querySelectorAll('[data-property], dl');
        for (const prop of properties) {
          if (prop.textContent?.includes('_gwp_product')) {
            return true;
          }
        }
        
        // If not found in DOM, check cart by variant ID
        const lineItemId = lineItem.id?.replace('CartItem-', '');
        if (!lineItemId) return false;
        
        const cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return false;
        
        const cart = await cartResponse.json();
        const cartItem = cart.items?.find(item => item.index.toString() === lineItemId);
        
        return cartItem?.variant_id === this.gwpVariantId;
      } catch (e) {
        return false;
      }
    }
    
    debouncedCheck(delay = 200) {
      clearTimeout(this.checkTimeout);
      this.checkTimeout = setTimeout(() => {
        this.checkAndAddGWP();
      }, delay);
    }

    customizeRebuyCartItems() {
      const rebuyGWPLineItem = document.querySelector(this.selectors.rebuyGWPItem);

      if (!rebuyGWPLineItem || rebuyGWPLineItem.hasAttribute('data-gwp-customized')) {
        return;
      }

      rebuyGWPLineItem.setAttribute('data-gwp-customized', 'true');

      // Remove quantity input
      const quantityWrapper = rebuyGWPLineItem.querySelector('.rebuy-cart__flyout-item-quantity');
      quantityWrapper?.remove();

      // Find the product title element
      const productTitle = rebuyGWPLineItem.querySelector('.rebuy-cart__flyout-item-product-title');
      if (!productTitle) return;

      // Check if note already exists
      let gwpNote = rebuyGWPLineItem.querySelector('.rebuy-cart__flyout-item-gwp-note');
      
      if (!gwpNote) {
        gwpNote = document.createElement('div');
        gwpNote.className = 'rebuy-cart__flyout-item-gwp-note';
        productTitle.insertAdjacentElement('afterend', gwpNote);
      }
      
      // Update note (create or update)
      gwpNote.style.color = this.gwpNoteColor;
      gwpNote.textContent = this.gwpNoteText;
    }
    
    startObservingRebuyCart() {
      // Stop existing observer if any
      if (this.rebuyObserver) {
        this.rebuyObserver.disconnect();
        this.rebuyObserver = null;
      }
      
      // Use cached container or find it
      if (!this.rebuyCartContainer) {
        this.rebuyCartContainer = document.querySelector(this.selectors.rebuyCart);
      }
      
      if (!this.rebuyCartContainer) {
        // Retry after a short delay if Rebuy cart isn't ready yet (max 3 retries)
        if (this.rebuyRetryCount < 3) {
          this.rebuyRetryCount++;
          setTimeout(() => {
            this.startObservingRebuyCart();
          }, 500);
        }
        return;
      }
      
      // Reset retry count on success
      this.rebuyRetryCount = 0;
      
      // Create MutationObserver to watch for GWP items being added
      this.rebuyObserver = new MutationObserver(() => {
        // Debounce the check to avoid excessive calls
        clearTimeout(this.observerTimeout);
        this.observerTimeout = setTimeout(() => {
          const rebuyGWPLineItem = document.querySelector(this.selectors.rebuyGWPItem);
          
          if (rebuyGWPLineItem && 
              !rebuyGWPLineItem.hasAttribute('data-gwp-customized') &&
              rebuyGWPLineItem.querySelector('.rebuy-cart__flyout-item-product-title')) {
            this.customizeRebuyCartItems();
          }
        }, 50);
      });
      
      // Start observing
      this.rebuyObserver.observe(this.rebuyCartContainer, {
        childList: true,
        subtree: true,
        attributes: false
      });
    }
    
    preventManualGWPAddition() {
      // Listen for clicks on Rebuy add-to-cart buttons
      document.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-rebuy-id], .rebuy-button, [data-product-id]');
        if (!addButton) return;
        
        // Check if the button is for the GWP variant
        const variantId = addButton.getAttribute('data-variant-id') || 
                         addButton.getAttribute('data-product-id') ||
                         addButton.closest('[data-variant-id]')?.getAttribute('data-variant-id');
        
        if (variantId && parseInt(variantId) === this.gwpVariantId) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          console.log('GWP: This item is automatically added with qualifying purchases.');
        }
      }, true); // Use capture phase
    }

    async checkAndAddGWP() {
      // Prevent concurrent checks
      if (this.isProcessing) {
        return;
      }
      
      this.isProcessing = true;
      this.hasCheckedOnce = true;
      
      try {
        // Fetch current cart state
        const cartResponse = await fetch('/cart.js');
        
        if (!cartResponse.ok) {
          console.error('GWP: Cart fetch failed', cartResponse.status);
          return;
        }
        
        const cart = await cartResponse.json().catch(() => null);
        
        if (!cart?.items || !Array.isArray(cart.items)) {
          console.error('GWP: Invalid cart structure', cart);
          return;
        }
        
        // Count non-GWP items and find GWP in cart
        let nonGWPItemCount = 0;
        let gwpInCart = null;
        
        for (const item of cart.items) {
          if (item.variant_id === this.gwpVariantId) {
            gwpInCart = item;
          } else {
            nonGWPItemCount += item.quantity || 0;
          }
        }
        
        const gwpManuallyRemoved = this.isGWPManuallyRemoved();
        
        // Add GWP if threshold is met and GWP not in cart and not manually removed
        if (nonGWPItemCount >= this.gwpThreshold && !gwpInCart && !gwpManuallyRemoved) {
          await this.addGWPToCart();
        } 
        // Remove GWP if threshold not met and GWP is in cart (including empty cart)
        else if ((nonGWPItemCount < this.gwpThreshold || nonGWPItemCount === 0) && gwpInCart) {
          await this.removeGWPFromCart(gwpInCart.key);
        }
        
        // Clear manual removal flag if cart becomes empty
        if (nonGWPItemCount === 0 && gwpManuallyRemoved) {
          this.clearGWPManuallyRemoved();
        }
      } catch (error) {
        console.error('GWP Error:', error);
      } finally {
        this.isProcessing = false;
      }
    }

    async addGWPToCart() {
      if (this.isAddingGWP) {
        return; // Already adding
      }
      
      this.isAddingGWP = true;
      
      const formData = new FormData();
      formData.append('id', this.gwpVariantId);
      formData.append('quantity', 1);
      
      // Add GWP properties
      formData.append('properties[_gwp_product]', 'true');
      if(this.gwpNoteText) {
        formData.append('properties[_gwp_note]', this.gwpNoteText);
        formData.append('properties[_gwp_note_color]', this.gwpNoteColor);
      }
      
      // Add sections to render (cart drawer, cart counter)
      formData.append('sections', 'cart-drawer,cart-counter');
      formData.append('sections_url', window.location.pathname);
      
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;
      
      try {
        const response = await fetch(`${window.routes.cart_add_url}`, config);
        
        if (!response.ok) {
          console.error('GWP: Failed to add to cart', response.status);
          this.isAddingGWP = false;
          return;
        }
        
        const data = await response.json();
        
        // Update cart UI
        this.updateCartUI(data);
        
        // Note: MutationObserver will automatically customize the GWP item when it appears in Rebuy cart
      } catch (error) {
        console.error('Error adding GWP:', error);
      } finally {
        // Reset flag after a delay to allow Rebuy to process
        setTimeout(() => {
          this.isAddingGWP = false;
        }, 1000);
      }
    }

    async removeGWPFromCart(lineItemKey) {
      if (this.isRemovingGWP) {
        return; // Already removing
      }
      
      this.isRemovingGWP = true;
      // This is automatic removal (threshold not met), don't set manual removal flag
      
      const body = JSON.stringify({
        updates: { [lineItemKey]: 0 },
        sections: ['cart-drawer', 'cart-counter'],
        sections_url: window.location.pathname
      });
      
      try {
        const response = await fetch(`${window.routes.cart_update_url}`, {
          ...fetchConfig(),
          body
        });
        
        if (!response.ok) {
          console.error('GWP: Failed to remove from cart', response.status);
          this.isRemovingGWP = false;
          return;
        }
        
        const data = await response.json();
        
        // Update cart UI
        this.updateCartUI(data);
      } catch (error) {
        console.error('Error removing GWP:', error);
      } finally {
        // Reset flag after a delay to allow Rebuy to process
        setTimeout(() => {
          this.isRemovingGWP = false;
        }, 1000);
      }
    }
    
    updateCartUI(data) {
      // Update cart drawer if it exists
      const cartDrawer = document.querySelector(this.selectors.cartDrawer);
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data, false);
      }
      
      // Trigger Rebuy cart refresh
      this.refreshRebuyCart();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
    
    refreshRebuyCart() {
      // Try to refresh Rebuy cart if available
      if (window.Rebuy?.cart) {
        try {
          const refreshMethod = window.Rebuy.cart.refresh || 
                               window.Rebuy.cart.reload || 
                               window.Rebuy.refresh;
          if (typeof refreshMethod === 'function') {
            refreshMethod();
          }
        } catch (e) {
          // Rebuy API might not be available - this is okay
        }
      }
      
      // Dispatch Rebuy events to trigger refresh
      try {
        document.dispatchEvent(new CustomEvent('rebuy:cart:refresh'));
        document.dispatchEvent(new CustomEvent('rebuy:smartcart.refresh'));
      } catch (e) {
        // Ignore if events don't work
      }
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new GWPManager();
    });
  } else {
    new GWPManager();
  }
})();